<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import CaptainDocumentAPI from 'dashboard/api/captain/document';
import CaptainResponseAPI from 'dashboard/api/captain/response';
import AssistantPlayground from 'dashboard/components-next/captain/assistant/AssistantPlayground.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AddContentDialog from './AddContentDialog.vue';
import { useI18n } from 'vue-i18n';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const assistantId = computed(() => Number(route.params.assistantId));
const isLoading = ref(true);
const documents = ref([]);
const documentCount = ref(0);
const responseCount = ref(0);
const addContentDialog = ref(null);

const portals = computed(() => store.getters['portals/allPortals'] || []);
const captainInboxes = computed(
  () => store.getters['captainInboxes/getRecords'] || []
);

const articleCount = computed(() =>
  portals.value.reduce(
    (total, portal) => total + Number(portal.meta?.published_count || 0),
    0
  )
);

const websiteCount = computed(
  () =>
    documents.value.filter(
      document => document.external_link && !document.pdf_document
    ).length
);

const fileCount = computed(
  () => documents.value.filter(document => document.pdf_document).length
);

const contentSources = computed(() => [
  {
    key: 'articles',
    name: t('CAPTAIN.KNOWLEDGE.SOURCES.ARTICLES.NAME'),
    description: t('CAPTAIN.KNOWLEDGE.SOURCES.ARTICLES.DESCRIPTION'),
    count: articleCount.value,
    icon: 'i-lucide-book-open',
    status: articleCount.value
      ? t('CAPTAIN.KNOWLEDGE.STATUS.ENABLED')
      : t('CAPTAIN.KNOWLEDGE.STATUS.NOT_CONFIGURED'),
    enabled: articleCount.value > 0,
    action: 'helpcenter',
  },
  {
    key: 'websites',
    name: t('CAPTAIN.KNOWLEDGE.SOURCES.WEBSITES.NAME'),
    description: t('CAPTAIN.KNOWLEDGE.SOURCES.WEBSITES.DESCRIPTION'),
    count: websiteCount.value,
    icon: 'i-lucide-globe-2',
    status: websiteCount.value
      ? t('CAPTAIN.KNOWLEDGE.STATUS.ENABLED')
      : t('CAPTAIN.KNOWLEDGE.STATUS.NOT_CONFIGURED'),
    enabled: websiteCount.value > 0,
    action: 'documents',
  },
  {
    key: 'documents',
    name: t('CAPTAIN.KNOWLEDGE.SOURCES.DOCUMENTS.NAME'),
    description: t('CAPTAIN.KNOWLEDGE.SOURCES.DOCUMENTS.DESCRIPTION'),
    count: fileCount.value,
    icon: 'i-lucide-paperclip',
    status: fileCount.value
      ? t('CAPTAIN.KNOWLEDGE.STATUS.ENABLED')
      : t('CAPTAIN.KNOWLEDGE.STATUS.NOT_CONFIGURED'),
    enabled: fileCount.value > 0,
    action: 'documents',
  },
  {
    key: 'answers',
    name: t('CAPTAIN.KNOWLEDGE.SOURCES.ANSWERS.NAME'),
    description: t('CAPTAIN.KNOWLEDGE.SOURCES.ANSWERS.DESCRIPTION'),
    count: responseCount.value,
    icon: 'i-lucide-message-square-text',
    status: responseCount.value
      ? t('CAPTAIN.KNOWLEDGE.STATUS.ENABLED')
      : t('CAPTAIN.KNOWLEDGE.STATUS.NOT_CONFIGURED'),
    enabled: responseCount.value > 0,
    action: 'responses',
  },
  {
    key: 'channels',
    name: t('CAPTAIN.KNOWLEDGE.SOURCES.CHANNELS.NAME'),
    description: t('CAPTAIN.KNOWLEDGE.SOURCES.CHANNELS.DESCRIPTION'),
    count: captainInboxes.value.length,
    icon: 'i-lucide-inbox',
    status: captainInboxes.value.length
      ? t('CAPTAIN.KNOWLEDGE.STATUS.DEPLOYED')
      : t('CAPTAIN.KNOWLEDGE.STATUS.NOT_DEPLOYED'),
    enabled: captainInboxes.value.length > 0,
    action: 'inboxes',
  },
]);

const loadKnowledge = async () => {
  isLoading.value = true;
  try {
    const [documentResponse, responseResponse] = await Promise.all([
      CaptainDocumentAPI.get({ assistantId: assistantId.value }),
      CaptainResponseAPI.get({
        assistantId: assistantId.value,
        status: 'approved',
      }),
      store.dispatch('portals/index'),
      store.dispatch('captainInboxes/get', {
        assistantId: assistantId.value,
      }),
    ]);
    documents.value = documentResponse.data.payload || [];
    documentCount.value = Number(documentResponse.data.meta?.total_count || 0);
    responseCount.value = Number(responseResponse.data.meta?.total_count || 0);
  } finally {
    isLoading.value = false;
  }
};

const openSource = action => {
  const routes = {
    helpcenter: 'portals_index',
    documents: 'captain_assistants_documents_index',
    responses: 'captain_assistants_responses_index',
    inboxes: 'captain_assistants_inboxes_index',
  };
  router.push({
    name: routes[action],
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};

const addContent = type => {
  const destinations = {
    article: { name: 'portals_index' },
    website: {
      name: 'captain_assistants_documents_index',
      query: { create: 'url' },
    },
    document: {
      name: 'captain_assistants_documents_index',
      query: { create: 'pdf' },
    },
    answer: {
      name: 'captain_assistants_responses_index',
      query: { create: '1' },
    },
    channel: { name: 'captain_assistants_inboxes_index' },
  };
  const destination = destinations[type];
  router.push({
    ...destination,
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};

watch(assistantId, loadKnowledge);
onMounted(loadKnowledge);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.KNOWLEDGE.HEADER')"
    :show-pagination-footer="false"
    :show-know-more="false"
    class="h-full"
  >
    <template #search>
      <Button
        :label="$t('CAPTAIN.KNOWLEDGE.ADD_CONTENT')"
        icon="i-lucide-plus"
        size="sm"
        @click="addContentDialog?.open()"
      />
    </template>

    <template #body>
      <div
        class="grid h-full min-h-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(32rem,1fr)_24rem]"
      >
        <section
          class="min-h-0 overflow-hidden rounded-lg outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <header class="border-b border-n-weak px-5 py-4">
            <h2 class="mb-1 text-base font-semibold text-n-slate-12">
              {{ $t('CAPTAIN.KNOWLEDGE.CONTENT_TITLE') }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-10">
              {{ $t('CAPTAIN.KNOWLEDGE.CONTENT_DESCRIPTION') }}
            </p>
          </header>

          <div
            v-if="isLoading"
            class="flex min-h-72 items-center justify-center"
          >
            <Spinner />
          </div>

          <div v-else class="divide-y divide-n-weak">
            <button
              v-for="source in contentSources"
              :key="source.key"
              type="button"
              class="grid w-full grid-cols-[2rem_minmax(0,1fr)_auto_auto] items-center gap-3 px-5 py-4 text-left hover:bg-n-alpha-1"
              @click="openSource(source.action)"
            >
              <span class="size-5 text-n-slate-11" :class="source.icon" />
              <span class="min-w-0">
                <span
                  class="block truncate text-sm font-medium text-n-slate-12"
                >
                  {{ source.name }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{ source.description }}
                </span>
              </span>
              <span class="text-sm tabular-nums text-n-slate-10">
                {{ source.count }}
              </span>
              <span
                class="inline-flex min-w-24 justify-center rounded-md px-2 py-0.5 text-xs font-medium outline outline-1 -outline-offset-1"
                :class="
                  source.enabled
                    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
                    : 'bg-n-slate-2 text-n-slate-10 outline-n-weak'
                "
              >
                {{ source.status }}
              </span>
            </button>
          </div>

          <footer
            class="flex items-center justify-between border-t border-n-weak bg-n-alpha-1 px-5 py-3"
          >
            <span class="text-xs text-n-slate-10">
              {{
                $t('CAPTAIN.KNOWLEDGE.SOURCE_TOTAL', {
                  count: documentCount + articleCount + responseCount,
                })
              }}
            </span>
            <Button
              :label="$t('CAPTAIN.KNOWLEDGE.REVIEW_ANSWERS')"
              variant="ghost"
              color="slate"
              size="sm"
              trailing-icon
              icon="i-lucide-arrow-right"
              @click="openSource('responses')"
            />
          </footer>
        </section>

        <aside class="hidden min-h-0 xl:block">
          <AssistantPlayground
            :assistant-id="assistantId"
            class="h-full bg-n-solid-1"
          />
        </aside>
      </div>
      <AddContentDialog ref="addContentDialog" @select="addContent" />
    </template>
  </PageLayout>
</template>
